import { SubscriptionStatusType } from '../enums/subscription-status.enum';
import { Deserializable } from './deserializable.model';
import { JsonObject } from './json-object.model';

/** Canonical premium access contract from API (premium_access object). */
export interface PremiumAccess {
  state?: string;
  has_access?: boolean;
  can_purchase?: boolean;
  offering_id?: string | null;
}

function parsePremiumAccess(input: unknown): PremiumAccess | undefined {
  if (input == null || typeof input !== 'object' || Array.isArray(input)) return undefined;
  const o = input as Record<string, unknown>;
  return {
    state: o['state'] as string | undefined,
    has_access: o['has_access'] as boolean | undefined,
    can_purchase: o['can_purchase'] as boolean | undefined,
    offering_id: (o['offering_id'] as string | null) ?? undefined,
  };
}

export class SubscriptionStatus implements Deserializable<JsonObject, SubscriptionStatus> {
  premiumAccess?: PremiumAccess;

  constructor(
    public access?: boolean,
    public manageSubscriptionUrl?: string,
    public canceledDate?: string,
    public status?: SubscriptionStatusType,
    public trialEndDate?: string,
    public trialStartDate?: string
  ) {}

  /** Canonical: has premium access (from premium_access.has_access or legacy access). */
  get canonicalHasAccess(): boolean {
    if (this.premiumAccess?.has_access !== undefined) return !!this.premiumAccess.has_access;
    return !!this.access;
  }

  /** Canonical: can purchase (from premium_access.can_purchase). */
  get canonicalCanPurchase(): boolean {
    return !!this.premiumAccess?.can_purchase;
  }

  /** Canonical offering id (from premium_access.offering_id). */
  get canonicalOfferingId(): string | null {
    const pa = this.premiumAccess?.offering_id;
    return pa != null && String(pa).trim() !== '' ? String(pa) : null;
  }

  deserialize(input: JsonObject): SubscriptionStatus {
    if (input == null) {
      return null;
    }

    this.access = !!input['access'];
    this.manageSubscriptionUrl = input['manage_subscription_url'] as string;
    this.canceledDate = input['canceledDate'] as string;
    this.status = input['status'] as SubscriptionStatusType;
    this.trialEndDate = input['trialEndDate'] as string;
    this.trialStartDate = input['trialStartDate'] as string;
    this.premiumAccess = parsePremiumAccess(input['premium_access']);

    return this;
  }
}
